import { buildClaimsFilingUrl } from '@/components/claims-filing/buildClaimsFilingUrl';
import type { Task } from '@/services/types/client-intake-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

export type PremisesFollowUpStepKey =
  | 'open_premises_claim'
  | 'research_alternate_contacts';

export type PremisesFollowUpTaskType =
  | CanonicalTaskType.OPEN_PREMISES_CLAIM
  | CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS;

const getTaskSortTimestamp = (task: Task): number => {
  const createdAtTimestamp = Date.parse(task.created_at || '');
  if (!Number.isNaN(createdAtTimestamp)) {
    return createdAtTimestamp;
  }

  const modifiedAtTimestamp = Date.parse(task.modified_at || '');
  if (!Number.isNaN(modifiedAtTimestamp)) {
    return modifiedAtTimestamp;
  }

  return 0;
};

export const isTaskForCoverage = (task: Task, coverageId: string): boolean =>
  task.coverage_id === coverageId || task.coverage?.id === coverageId;

export const getLatestTaskByCanonicalType = (
  tasks: Task[],
  type: CanonicalTaskType,
): Task | undefined =>
  tasks
    .filter((task) => task.type?.canonical_type === type)
    .sort((left, right) => {
      return getTaskSortTimestamp(right) - getTaskSortTimestamp(left);
    })[0];

export const getLatestCoverageTaskByCanonicalType = (
  tasks: Task[],
  coverageId: string,
  type: CanonicalTaskType,
): Task | undefined =>
  getLatestTaskByCanonicalType(
    tasks.filter((task) => isTaskForCoverage(task, coverageId)),
    type,
  );

export const getLatestPremisesSendLorTask = (
  tasks: Task[],
): Task | undefined => getLatestTaskByCanonicalType(tasks, CanonicalTaskType.SEND_LOR);

const getLatestPremisesFollowUpTask = (tasks: Task[]): Task | undefined =>
  tasks
    .filter((task) =>
      task.type?.canonical_type === CanonicalTaskType.OPEN_PREMISES_CLAIM ||
      task.type?.canonical_type === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS,
    )
    .sort((left, right) => {
      return getTaskSortTimestamp(right) - getTaskSortTimestamp(left);
    })[0];

export const getCurrentPremisesFollowUpTask = (
  tasks: Task[],
): Task | undefined => {
  const latestFollowUpTask = getLatestPremisesFollowUpTask(tasks);
  if (!latestFollowUpTask) {
    return undefined;
  }

  const latestSendLorTask = getLatestPremisesSendLorTask(tasks);
  if (!latestSendLorTask) {
    return latestFollowUpTask;
  }

  const latestFollowUpCreatedAt = getTaskSortTimestamp(latestFollowUpTask);
  const latestSendLorCreatedAt = getTaskSortTimestamp(latestSendLorTask);

  if (latestSendLorCreatedAt > latestFollowUpCreatedAt) {
    return undefined;
  }

  return latestFollowUpTask;
};

export const isPremisesFollowUpTask = (task: Task | undefined): boolean =>
  task?.type?.canonical_type === CanonicalTaskType.OPEN_PREMISES_CLAIM ||
  task?.type?.canonical_type === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS;

export const getPremisesFollowUpTaskType = (
  task: Task | undefined,
): PremisesFollowUpTaskType | null => {
  if (task?.type?.canonical_type === CanonicalTaskType.OPEN_PREMISES_CLAIM) {
    return CanonicalTaskType.OPEN_PREMISES_CLAIM;
  }
  if (
    task?.type?.canonical_type === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS
  ) {
    return CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS;
  }
  return null;
};

export const getPremisesFollowUpStepKey = (
  task: Task | undefined,
): PremisesFollowUpStepKey | null => {
  if (task?.type?.canonical_type === CanonicalTaskType.OPEN_PREMISES_CLAIM) {
    return 'open_premises_claim';
  }
  if (
    task?.type?.canonical_type === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS
  ) {
    return 'research_alternate_contacts';
  }
  return null;
};

export const getPremisesFollowUpStepKeyForType = (
  taskType: PremisesFollowUpTaskType | null | undefined,
): PremisesFollowUpStepKey | null => {
  if (taskType === CanonicalTaskType.OPEN_PREMISES_CLAIM) {
    return 'open_premises_claim';
  }
  if (taskType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS) {
    return 'research_alternate_contacts';
  }
  return null;
};

export const getPremisesFollowUpLabelForType = (
  taskType: PremisesFollowUpTaskType | null | undefined,
): string | null => {
  if (taskType === CanonicalTaskType.OPEN_PREMISES_CLAIM) {
    return 'Open Premises Claim';
  }
  if (taskType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS) {
    return 'Research Alternate Contacts';
  }
  return null;
};

export const getPremisesFollowUpLabel = (task: Task | undefined): string | null =>
  getPremisesFollowUpLabelForType(getPremisesFollowUpTaskType(task));

export const isTaskCompleted = (task: Task | undefined): boolean =>
  task?.status?.canonical_status === CanonicalTaskStatus.COMPLETED ||
  Boolean(task?.completed_at);

export const isTaskNotApplicable = (task: Task | undefined): boolean =>
  task?.status?.canonical_status === CanonicalTaskStatus.NOT_APPLICABLE;

export const buildPremisesFollowUpUrlForType = (
  matterId: string,
  coverageId: string,
  taskType: PremisesFollowUpTaskType | null | undefined,
): string | null => {
  if (taskType === CanonicalTaskType.OPEN_PREMISES_CLAIM) {
    return buildClaimsFilingUrl(matterId, coverageId, 'call');
  }
  if (taskType === CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS) {
    return `/matters/${matterId}/premises/${coverageId}/research-property`;
  }
  return null;
};

export const buildPremisesFollowUpUrl = (
  matterId: string,
  coverageId: string,
  task: Task | undefined,
): string | null =>
  buildPremisesFollowUpUrlForType(
    matterId,
    coverageId,
    getPremisesFollowUpTaskType(task),
  );
